import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { setActivePanel, setProjectId, triggerRefreshTasks } from '../../../store/popupState';
import { getProjectsTasks, postProjectsTaskStatus } from '../../../services/projects';
import { Button } from '@nextui-org/react';
import { FiPlus } from 'react-icons/fi';
import GorevlerCardComponent from '../../GorevlerCardComponent';
import Notiflix from 'notiflix';

// Mock priority list
const PriorityList = [
  { key: '1', label: 'Çok Düşük', color: '#cddc39' },
  { key: '2', label: 'Düşük', color: '#8bc34a' },
  { key: '3', label: 'Normal', color: '#2196f3' },
  { key: '4', label: 'Yüksek', color: '#ff9800' },
  { key: '5', label: 'Çok Yüksek', color: '#f44336' }
];

const statusColumns = [
  { id: 1, title: 'Yeni', color: 'bg-gray-50 dark:bg-gray-800', borderColor: 'border-gray-200 dark:border-gray-700', textColor: 'text-gray-700 dark:text-gray-300' },
  { id: 2, title: 'İşlemde', color: 'bg-blue-50 dark:bg-blue-900/20', borderColor: 'border-blue-200 dark:border-blue-700', textColor: 'text-blue-700 dark:text-blue-300' },
  { id: 3, title: 'Hazır', color: 'bg-yellow-50 dark:bg-yellow-900/20', borderColor: 'border-yellow-200 dark:border-yellow-700', textColor: 'text-yellow-700 dark:text-yellow-300' },
  { id: 4, title: 'İncelemede', color: 'bg-purple-50 dark:bg-purple-900/20', borderColor: 'border-purple-200 dark:border-purple-700', textColor: 'text-purple-700 dark:text-purple-300' },
  { id: 5, title: 'Tamamlandı', color: 'bg-green-50 dark:bg-green-900/20', borderColor: 'border-green-200 dark:border-green-700', textColor: 'text-green-700 dark:text-green-300' }
];

const KanbanComponent = ({ projectId }) => {
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const refreshTasksCounter = useSelector((state) => state.popupState.refreshTasksCounter);

  // Görev listesini yenileme fonksiyonu
  const refreshTaskList = useCallback(async (showLoading = false) => {
    if (!projectId) return;
    
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const response = await getProjectsTasks({ slug: projectId });
      setTaskData(response);
    } catch (err) {
      setError('Görevler yüklenirken bir hata oluştu');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [projectId]);

  // Component mount olduğunda ve projectId değiştiğinde görevleri yükle
  useEffect(() => {
    refreshTaskList(true); // İlk yüklemede loading göster
  }, [refreshTaskList]);

  // refreshTasksCounter değiştiğinde görevleri yenile
  useEffect(() => {
    if (refreshTasksCounter > 0) {
      refreshTaskList(false); // Yenileme işlemlerinde loading gösterme
    }
  }, [refreshTasksCounter, refreshTaskList]);

  // Görevleri durumlara göre grupla
  const groupTasksByStatus = (tasks) => {
    const grouped = {};
    statusColumns.forEach(column => {
      grouped[column.id] = tasks.filter(task => task.tstatus === column.id);
    });
    return grouped;
  };

  // Yeni görev ekleme
  const handleAddTask = () => {
    dispatch(setProjectId(projectId));
    dispatch(setActivePanel([6, 0, null])); // Yeni görev oluşturma
  };

  // Drag & Drop işlemi
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Eğer hedef yoksa işlemi iptal et
    if (!destination) {
      return;
    }

    // Eğer aynı sütunda aynı pozisyondaysa işlemi iptal et
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Görev ID'sini al
    const taskId = parseInt(draggableId);
    const newStatus = parseInt(destination.droppableId);

    // Optimistic update - önce UI'da değişikliği yap
    if (taskData && taskData.tasks) {
      const updatedTasks = taskData.tasks.map(task => 
        task.id === taskId ? { ...task, tstatus: newStatus } : task
      );
      setTaskData({ ...taskData, tasks: updatedTasks });
    }

    try {
      // API'ye durum güncelleme isteği gönder (sessizce)
      await postProjectsTaskStatus({
        slug: projectId,
        taskid: taskId,
        tstatus: newStatus
      });

      // API başarılı olduğunda veriyi yenile (opsiyonel)
      // dispatch(triggerRefreshTasks());
      
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      
      // Hata durumunda UI'ı eski haline getir
      if (taskData && taskData.tasks) {
        const revertedTasks = taskData.tasks.map(task => 
          task.id === taskId ? { ...task, tstatus: parseInt(source.droppableId) } : task
        );
        setTaskData({ ...taskData, tasks: revertedTasks });
      }
      
      // Hata durumunda kullanıcıya bildirim göster
      Notiflix.Notify.failure('Görev durumu güncellenirken bir hata oluştu!');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Görevler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <Button 
          onClick={refreshTaskList}
          className="mt-4"
          color="primary"
        >
          Tekrar Dene
        </Button>
      </div>
    );
  }

  if (!taskData || !taskData.tasks || taskData.tasks.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Henüz görev yok</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Bu projeye ilk görevi ekleyerek başlayın</p>
          <Button 
            onClick={handleAddTask}
            color="primary"
            startContent={<FiPlus size={16} />}
          >
            İlk Görevi Ekle
          </Button>
        </div>
      </div>
    );
  }

  const groupedTasks = groupTasksByStatus(taskData.tasks);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Ara..."
                className="block w-64 pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Team Avatars */}
            <div className="flex items-center space-x-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-xs font-semibold">
                    {i}
                  </div>
                ))}
                <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-600 border-2 border-white dark:border-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 text-xs font-semibold">
                  +4
                </div>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={handleAddTask}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            startContent={<FiPlus size={16} />}
          >
            Yeni Görev
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="p-6">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 pb-4">
            {statusColumns.map((column) => (
              <div 
                key={column.id} 
                className={`w-full ${column.color} border ${column.borderColor} rounded-xl p-4 shadow-sm`}
              >
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <h3 className={`font-semibold text-base ${column.textColor}`}>
                      {column.title}
                    </h3>
                    <span className={`${column.textColor} bg-white dark:bg-gray-800 text-xs px-2 py-1 rounded-full border ${column.borderColor}`}>
                      {groupedTasks[column.id]?.length || 0}
                    </span>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                    </svg>
                  </button>
                </div>

              {/* Droppable Area */}
              <Droppable droppableId={column.id.toString()}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`space-y-3 min-h-[400px] transition-colors ${
                      snapshot.isDraggingOver ? 'bg-opacity-50' : ''
                    }`}
                  >
                    {groupedTasks[column.id]?.map((task, index) => (
                      <Draggable
                        key={task.id}
                        draggableId={task.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-grab active:cursor-grabbing border border-gray-200 dark:border-gray-700 mb-3 ${
                              snapshot.isDragging ? 'shadow-lg rotate-1 scale-105' : ''
                            }`}
                            onClick={() => dispatch(setActivePanel([6, 1, task.id]))}
                          >
                            {/* Task Card Content */}
                            <div className="p-3">
                              {/* Task Title */}
                              <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-2 line-clamp-2">
                                {task.tsubject}
                              </h4>
                              
                              {/* Task Meta */}
                              <div className="space-y-2">
                                {/* Deadline & Priority Row */}
                                <div className="flex items-center justify-between">
                                  {task.deadline_at && (
                                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      {(() => {
                                        const today = new Date();
                                        const deadline = new Date(task.deadline_at);
                                        const diffTime = deadline - today;
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                        
                                        if (diffDays < 0) return 'Gecikti';
                                        if (diffDays === 0) return 'Bugün';
                                        if (diffDays === 1) return '1 gün';
                                        if (diffDays <= 7) return `${diffDays} gün`;
                                        return `${Math.ceil(diffDays / 7)} hafta`;
                                      })()}
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center">
                                    <div 
                                      className="w-2 h-2 rounded-full mr-1"
                                      style={{ backgroundColor: PriorityList[task.tpriority - 1]?.color || '#2196f3' }}
                                    />
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {PriorityList[task.tpriority - 1]?.label || 'Normal'}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Assignee & Actions */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    {task.uimg && task.uimgtype ? (
                                      <img
                                        src={`${process.env.NEXT_PUBLIC_USERIMG}${task.uimg}.${task.uimgtype}`}
                                        alt={`${task.ufname} ${task.ulname}`}
                                        className="h-5 w-5 rounded-full border border-gray-200 dark:border-gray-600"
                                      />
                                    ) : (
                                      <div className="h-5 w-5 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-semibold">
                                        {task.ufname?.charAt(0)}{task.ulname?.charAt(0)}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Action Buttons */}
                                  <div className="flex space-x-1">
                                    <button 
                                      className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(setActivePanel([6, 1, task.id]));
                                      }}
                                    >
                                      Görüntüle
                                    </button>
                                    {column.id === 1 && (
                                      <button 
                                        className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Approve logic here
                                        }}
                                      >
                                        Onayla
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    
                    {provided.placeholder}
                    
                    {/* Empty State */}
                    {(!groupedTasks[column.id] || groupedTasks[column.id].length === 0) && (
                      <div className="text-center py-8">
                        {column.id === 4 ? (
                          // İncelemede sütunu için özel görsel
                          <div className="space-y-3">
                            <div className="mx-auto w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Şu anda incelenen görev bulunmuyor.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Bu durumda görev yok
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Görevleri buraya sürükleyin
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
};

export default KanbanComponent;
